import { useState, useEffect, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from './useAuth';
import { useDatabase } from './useDatabase';
import { WorkspaceContext } from '../context/WorkspaceContext';

/**
 * Resolves the current user's Person in this workspace.
 *
 * Currently stored in localStorage per workspace+user. When the API
 * supports a user-to-person mapping endpoint, swap the storage here.
 */
export function useCurrentPerson() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { user } = useAuth();
  const { database } = useDatabase();

  const storageKey = workspaceId && user?.userId
    ? `chronomap-person-${workspaceId}-${user.userId}`
    : null;

  const [personId, setPersonIdState] = useState<string | null>(() => {
    if (!storageKey) return null;
    return localStorage.getItem(storageKey);
  });

  // Re-read from storage when workspace/user changes
  useEffect(() => {
    if (!storageKey) {
      setPersonIdState(null);
      return;
    }
    setPersonIdState(localStorage.getItem(storageKey));
  }, [storageKey]);

  // Validate that the stored person still exists in the database
  const [validated, setValidated] = useState(false);
  useEffect(() => {
    if (!database || !personId) {
      setValidated(!personId); // valid if no mapping (we'll show the prompt)
      return;
    }
    const people = database.people();
    if (!people[personId]) {
      // Person was deleted — clear the mapping
      if (storageKey) localStorage.removeItem(storageKey);
      setPersonIdState(null);
    }
    setValidated(true);
  }, [database, personId, storageKey]);

  const setPersonId = useCallback((id: string | null) => {
    if (!storageKey) return;
    if (id) {
      localStorage.setItem(storageKey, id);
    } else {
      localStorage.removeItem(storageKey);
    }
    setPersonIdState(id);
  }, [storageKey]);

  // Get person's team IDs
  const { safeData } = useContext(WorkspaceContext);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [stakeholderIds, setStakeholderIds] = useState<string[]>([]);

  useEffect(() => {
    if (!database || !personId || !safeData) {
      setTeamIds([]);
      setStakeholderIds([]);
      return;
    }

    const people = database.people();
    const person = people[personId];
    if (!person) {
      setTeamIds([]);
      setStakeholderIds([]);
      return;
    }

    // Get teams this person belongs to
    person.team_ids(safeData).then((ids: string[]) => setTeamIds(ids));

    // Get stakeholders where this person is a representative
    const allStakeholders = database.stakeholders();
    const repStakeholderIds: string[] = [];
    for (const [sid] of Object.entries(allStakeholders)) {
      const repIds = database.representatives_for_stakeholder(sid);
      if (repIds.includes(personId)) {
        repStakeholderIds.push(sid);
      }
    }
    setStakeholderIds(repStakeholderIds);
  }, [database, personId, safeData]);

  return {
    personId,
    setPersonId,
    teamIds,
    stakeholderIds,
    loading: !validated,
    isMapped: !!personId,
  };
}
